/* eslint-disable no-lonely-if */
import { RouteComponentProps } from "@reach/router";
import {
  FormEvent,
  FunctionComponent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import Button from "../components/button/Button";
import ContextMenu, {
  ContextOption
} from "../components/context-menu/ContextMenu";
import Input, { TextArea } from "../components/input/Input";
import Table, { Column } from "../components/table/Table";
import {
  allSchemes,
  AppKey,
  AppKeyCreate,
  AppKeyImport,
  CertifcateInfo,
  Issuer,
  IssuerCreate,
  KeyCategory,
  Scheme
} from "../utils/helpers/types";
import { allMonths, initialPaginationData } from "../utils/constants";
import { resources } from "../utils/resources";
import {
  autoGenerateKeyValue,
  createIssuer,
  createIssuerKey,
  downloadKeyRequest,
  generateRSAValue,
  getCertificateInfo,
  getIssuerKeys,
  getIssuers,
  importIssuerKeyValue,
  toggleKeyActivation,
  updateIssuer,
  updateIssuerKey,
  uploadSignedCertificate
} from "../utils/helpers/data/issuer-management";
import LayoutContext from "../context/layout-context";
import styles from "./issuer-management.module.scss";
import Modal from "../components/modal/Modal";
import Select, { Option } from "../components/select/Select";
import {
  getCardinalStr,
  getOptionsFromArray
} from "../utils/helpers/type-conversions";
import TabbedView from "../components/tabs/TabbedView";
import FileUploader from "../components/file-uploader/FileUploader";
import { getCAKeys } from "../utils/helpers/data/certificate-authority";
import { getKeyCategories } from "../utils/helpers/data/key-categories";
import { isKeyZMK } from "../utils/helpers/validation-helpers";
import { axiosInstance } from "../utils/helpers/axios";

let timerRef: NodeJS.Timeout;

const FintechManagement: FunctionComponent<RouteComponentProps> = () => {
  const [searchStr, setSearchStr] = useState("");
  const [code, setCode] = useState("");
  const [pagination, setPagination] = useState({ ...initialPaginationData });
  const [dataSource, setDataSource] = useState<Issuer[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [issuerOnEdit, setIssuerOnEdit] = useState<Issuer | null>(null);
  const [caKeyOptions, setCaKeyOptions] = useState<Option[]>([]);

  const { notify } = useContext(LayoutContext);

  const fetchIssuers = async (pageNumber = pagination.pageNumber) => {
    setLoading(true);
    const { data, message, error } = await getIssuers({
      ...pagination,
      pageNumber,
      searchStr,
      code
    });
    setLoading(false);
    if (error || !data) {
      notify("error", `Unable to fetch issuers: ${message}`);
      return;
    }
    setDataSource(data.issuers);
    setPagination({ ...pagination, total: data.count, pageNumber });
  };

  useEffect(() => {
    clearTimeout(timerRef);
    timerRef = setTimeout(() => {
      fetchIssuers(1);
    }, 400);
  }, [searchStr, code]);

  useEffect(() => {
    fetchIssuers();
  }, [pagination.pageNumber, pagination.pageSize]);

  const columns: Column<Issuer>[] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name"
    },
    {
      title: "Issuer code",
      dataIndex: "issuerCode",
      key: "issuerCode"
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (desc: string) => (
        <span>
          {desc.slice(0, 25)}
          {desc.length > 25 ? ". . ." : ""}
        </span>
      )
    },
    {
      title: "",
      render: (_, record) => (
        <ContextMenu
          options={[
            { label: "Edit", onClick: () => displayAddEditModal(record) }
          ]}
        />
      ),
      absorbEvents: true
    }
  ];

  const displayAddEditModal = (issuer: Issuer | null) => {
    setIssuerOnEdit(issuer);
    setShowAddEditModal(true);
  };

  const dismissAddEditModal = () => {
    setIssuerOnEdit(null);
    setShowAddEditModal(false);
  };

  const fetchCAKeys = async () => {
    setLoading(true);
    const { error, message, data } = await getCAKeys({
      pageNumber: 1,
      pageSize: 1000
    });
    setLoading(false);
    if (error || !data) {
      notify("error", `Unable to fetch certificate authority keys: ${message}`);
      return;
    }
    setCaKeyOptions(
      data.keys.map(
        key =>
          ({
            label: key.keyLabel,
            value: key.id
          } as Option)
      )
    );
  };

  useEffect(() => {
    fetchCAKeys();
  }, []);

  return (
    <main>
      <h1 className="title vertical-margin spaced thin">Issuer Management</h1>
      <div className="flex end">
        <Button onClick={() => displayAddEditModal(null)}>New Issuer</Button>
      </div>

      <div
        style={{
          display: "flex"
        }}
      >
        <Input
          value={searchStr}
          onChange={setSearchStr}
          placeholder="Search by issuer name"
          className="block vertical-margin"
          icon={resources.icons.SEARCH}
          size="large"
        />

        <Input
          value={code}
          onChange={setCode}
          placeholder="Search by code"
          className="block vertical-margin"
          icon={resources.icons.SEARCH}
          size="large"
        />
      </div>

      <Table
        dataSource={dataSource}
        columns={columns}
        loading={loading}
        pagination={pagination}
        rowExpandRender={(row, isMounted) =>
          isMounted && <KeyRender issuer={row} caKeyOptions={caKeyOptions} />
        }
        onPageChange={pageNumber =>
          setPagination({ ...pagination, pageNumber })
        }
        onPageSizeChange={pageSize =>
          setPagination({ ...pagination, pageSize, pageNumber: 1 })
        }
        expandOnRowClick
      />

      <AddEditModal
        visible={showAddEditModal}
        cancel={() => dismissAddEditModal()}
        issuer={issuerOnEdit}
        onComplete={stayOnPage =>
          fetchIssuers(stayOnPage ? pagination.pageNumber : 1)
        }
      />
    </main>
  );
};

const blankIssuer: IssuerCreate = {
  name: "",
  description: "",
  issuerCode: ""
};

interface AddEditProps {
  visible: boolean;
  cancel: () => void;
  onComplete: (stayOnPage: boolean) => void;
  issuer: Issuer | null;
}

const AddEditModal: FunctionComponent<AddEditProps> = ({
  visible,
  cancel,
  issuer,
  onComplete
}) => {
  const [formData, setFormData] = useState({ ...blankIssuer });
  const [loading, setLoading] = useState(false);

  const { notify } = useContext(LayoutContext);

  const handleChange = (key: keyof IssuerCreate, value: unknown) => {
    setFormData({ ...formData, [key]: value });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error, message } = await (issuer
      ? updateIssuer({ ...issuer, ...formData })
      : createIssuer(formData));
    setLoading(false);
    if (error) {
      notify(
        "error",
        `Unable to ${issuer ? "update" : "create"} issuer: ${message}`
      );
    } else {
      notify(
        "success",
        `Issuer ${issuer ? "updated" : "created"} successfully`
      );
      onComplete(Boolean(issuer));
      cancel();
    }
  };

  useEffect(() => {
    setFormData({ ...blankIssuer, ...(issuer || {}) });
  }, [issuer, visible]);

  return (
    <Modal visible={visible} cancel={cancel} size="small">
      <form onSubmit={handleSubmit}>
        <div className="input-group">
          <span className="question">Issuer name</span>
          <Input
            value={formData.name}
            placeholder="Enter name"
            onChange={value => handleChange("name", value)}
            required
            responsive
          />
        </div>

        <div className="input-group">
          <span className="question">Issuer Code</span>
          <Input
            value={formData.issuerCode}
            placeholder="Enter issuer code"
            onChange={value => handleChange("issuerCode", value)}
            disabled={Boolean(issuer)}
            inputValidation={{
              required: true,
              transform: val =>
                val
                  .toUpperCase()
                  .replace(/[^A-Z]/g, "")
                  .slice(0, 8)
            }}
            responsive
          />
        </div>

        <div className="input-group">
          <span className="question">Description</span>
          <TextArea
            value={formData.description}
            placeholder="Enter description"
            onChange={value => handleChange("description", value)}
          />
        </div>

        <div className="flex spaced end">
          <Button type="accent" onClick={() => cancel?.()}>
            Cancel
          </Button>
          <Button buttonType="submit" loading={loading}>
            {issuer ? "Update" : "Create"} Issuer
          </Button>
        </div>
      </form>
    </Modal>
  );
};

interface KeyRenderProps {
  issuer: Issuer;
  caKeyOptions: Option[];
}

const KeyRender: FunctionComponent<KeyRenderProps> = ({
  issuer,
  caKeyOptions
}) => {
  const [loading, setLoading] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [showImportModal, setImportModal] = useState(false);
  const [showCertificateGenerationModal, setShowCertificateGenerationModal] =
    useState(false);
  const [showCertificateUploadModal, setShowCertificateUploadModal] =
    useState(false);
  const [showCertificateViewModal, setShowCertificateViewModal] =
    useState(false);
  const [keyOnEdit, setKeyOnEdit] = useState<AppKey | null>(null);
  const [keyOnView, setKeyOnView] = useState<AppKey | null>(null);
  const [activeTab, setActiveTab] = useState<string | number>("des");
  const [transportKeyOptions, setTransportKeyOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [desPagination, setDesPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    total: 0
  });
  const [rsaPagination, setRsaPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    total: 0
  });
  const [deskeys, setDesKeys] = useState<AppKey[]>([]);
  const [rsakeys, setRsaKeys] = useState<AppKey[]>([]);

  const { notify, confirm } = useContext(LayoutContext);

  const handleTabChange = (tabKey: string | number) => {
    setActiveTab(tabKey);
    fetchIssuerKeys(tabKey);
  };

  const fetchIssuerKeys = async (tabKey?: string | number) => {
    if (!issuer) {
      return;
    }
    setLoading(true);

    try {
      const pagination = tabKey === "des" ? desPagination : rsaPagination;

      const { data } = await getIssuerKeys(
        issuer.issuerCode,
        pagination.pageNumber,
        pagination.pageSize,
        tabKey === "des" ? "DES" : "RSA"
      );

      if (tabKey === "des") {
        const zmkResponse = await axiosInstance.get(
          `/des-key/get-issuer-keys?issuerCode=${issuer.issuerCode}&page=1&size=0&keyCategory=ZMK`
        );

        const transportOptions = (zmkResponse.data.content || []).map(
          (key: any) => ({
            label: key.keyLabel,
            value: key.keyLabel
          })
        );

        console.log("Transport Options:", transportOptions);

        setTransportKeyOptions(transportOptions);

        setDesKeys(data?.keys || []);
        setDesPagination({ ...desPagination, total: data?.total || 0 });
      } else {
        setRsaKeys(data?.keys || []);
        setRsaPagination({ ...rsaPagination, total: data?.total || 0 });
      }
    } catch (err) {
      notify("error", `Unable to fetch issuer's keys: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIssuerKeys(activeTab);
  }, [
    issuer,
    activeTab,
    desPagination.pageNumber,
    desPagination.pageSize,
    rsaPagination.pageNumber,
    rsaPagination.pageSize
  ]);

  const handleAutoGenerate = async (key: AppKey) => {
    const { error, message } = await autoGenerateKeyValue(key);
    if (error) {
      notify("error", `Unable to autogenerate key value: ${message}`);
      return;
    }
    fetchIssuerKeys(activeTab);
  };

  const validateKeys = async (keyLabel: string) => {
    try {
      const { data } = await axiosInstance.post(`/des-key/validate`, {
        keyLabel
      });

      if (data.responseCode === "00") {
        notify("success", data.responseMessage);
      } else {
        notify("error", `Validation failed: ${data.responseMessage}`);
      }
    } catch (error: any) {
      notify(
        "error",
        error.response?.data?.responseMessage ||
          "An error occurred during validation."
      );
    }
  };

  const displayCertificateGenerationModal = (key: AppKey) => {
    setKeyOnEdit(key);
    setShowCertificateGenerationModal(true);
  };

  const dismissCertificateGenerationModal = () => {
    setKeyOnEdit(null);
    setShowCertificateGenerationModal(false);
  };

  const displayCertificateViewModal = (key: AppKey) => {
    setKeyOnEdit(key);
    setShowCertificateViewModal(true);
  };

  const dismissCertificateViewModal = () => {
    setKeyOnEdit(null);
    setShowCertificateViewModal(false);
  };

  const displayCertificateUploadModal = (key: AppKey) => {
    setKeyOnEdit(key);
    setShowCertificateUploadModal(true);
  };

  const dismissCertificateUploadModal = () => {
    setKeyOnEdit(null);
    setShowCertificateUploadModal(false);
  };

  const handleStatusToggle = async (key: AppKey, activated: boolean) => {
    const { error, message } = await toggleKeyActivation(key, activated);
    if (error) {
      notify("error", `Unable to toggle key status: ${message}`);
      return;
    }
    fetchIssuerKeys(activeTab);
  };

  const columns: Column<AppKey>[] = [
    {
      title: "Label",
      key: "keyLabel",
      dataIndex: "keyLabel"
    },
    {
      title: "Category",
      key: "keyCategory",
      dataIndex: "keyCategory"
    },
    {
      title: "Environment",
      key: "envFlag",
      dataIndex: "envFlag",
      render: value => (
        <span className={`tag ${value === "LIVE" ? "success" : "error"}`}>
          {value}
        </span>
      )
    },
    { title: "KCV", key: "kcv", dataIndex: "kcv" },
    {
      title: "Status Flags",
      key: "keyValue",
      dataIndex: "keyValue",
      render: (_, key) => (
        <span className="flex spaced wrap">
          {key.hasKey && <strong className="smaller tag">HAS VALUE</strong>}
          {key.activated && (
            <strong className="smaller tag palegreen">ACTIVATED</strong>
          )}
        </span>
      )
    },
    {
      title: "",
      dataIndex: "id",
      key: "id",
      render: (_, key) => (
        <ContextMenu
          options={
            [
              {
                label: "Edit",
                onClick: () => displayAddEditModal(key)
              },
              key.keyCategory !== "RSA" && {
                label: "Import value",
                onClick: () => displayImportModal(key)
              },
              key.keyCategory !== "RSA" && {
                label: "Auto Generate",
                onClick: () =>
                  confirm({
                    title: `Do you want to auto-generate value for the key: ${key.keyLabel}`,
                    body: "This key already has an existing value which will be overwritten if you proceed",
                    onOk: async () => handleAutoGenerate(key)
                  })
              },
              key.keyCategory === "RSA" &&
                !key.hasKey &&
                !key.activated && {
                  label: "Generate RSA value",
                  onClick: () => displayCertificateGenerationModal(key)
                },
              key.keyCategory === "RSA" &&
                key.hasKey &&
                !key.activated && {
                  label: "Generate RSA value",
                  onClick: () =>
                    confirm({
                      title: `Do you want to generate RSA value for the key: ${key.keyLabel}`,
                      body: "This action will change current key value and existing key certificates will no longer be valid. Are you sure you want to continue?",
                      onOk: async () => displayCertificateGenerationModal(key)
                    })
                },
              key.keyCategory !== "RSA" &&
                key.hasKey && {
                  label: "View Key Value",
                  onClick: () => setKeyOnView(key)
                },
              {
                label: "Validate Key",
                onClick: () => {
                  validateKeys(key.keyLabel);
                }
              },
              !key.activated &&
                key.hasKey && {
                  label: <strong className="success">Activate</strong>,
                  onClick: () =>
                    confirm({
                      title: `Do you want to activate the key: ${key.keyLabel}`,
                      body: "",
                      onOk: async () => handleStatusToggle(key, true)
                    })
                },
              key.activated && {
                label: <strong className="danger">Deactivate</strong>,
                onClick: () =>
                  confirm({
                    title: `Do you want to deactivate the key: ${key.keyLabel}`,
                    body: "",
                    onOk: async () => handleStatusToggle(key, false)
                  })
              },
              key.keyCategory === "RSA" &&
                key.hasKey && {
                  label: "Download certificate",
                  onClick: () => displayCertificateGenerationModal(key)
                },
              key.keyCategory === "RSA" &&
                key.hasKey && {
                  label: "Upload signed certificate",
                  onClick: () => displayCertificateUploadModal(key)
                },
              key.keyCategory === "RSA" &&
                key.hasKey &&
                key.hasKey && {
                  label: "View certificate info",
                  onClick: () => displayCertificateViewModal(key)
                }
            ].filter(Boolean) as ContextOption[]
          }
        />
      )
    }
  ];

  const displayAddEditModal = (key: AppKey | null) => {
    setKeyOnEdit(key);
    setShowAddEditModal(true);
  };

  const dismissAddEditModal = () => {
    setKeyOnEdit(null);
    setShowAddEditModal(false);
  };

  const displayImportModal = (key: AppKey | null) => {
    setKeyOnEdit(key);
    setImportModal(true);
  };

  const dismissImportModal = () => {
    setKeyOnEdit(null);
    setImportModal(false);
  };

  return (
    <div className={styles.expanded}>
      <h2 className="margin-bottom thin">
        Keys for {issuer?.name} (
        {activeTab === "des" ? deskeys.length : rsakeys.length})
      </h2>
      <div className="flex end vertical-margin spaced">
        <Button size="small" onClick={() => displayAddEditModal(null)}>
          New Key
        </Button>
      </div>
      <TabbedView
        defaultKey={activeTab}
        onTabChange={handleTabChange}
        tabs={[
          {
            tabTitle: "Des Keys",
            tabKey: "des",
            tabContent: (
              <Table
                columns={columns}
                dataSource={deskeys}
                pagination={desPagination}
                loading={loading}
                onPageChange={pageNumber =>
                  setDesPagination({ ...desPagination, pageNumber })
                }
                onPageSizeChange={pageSize =>
                  setDesPagination({
                    ...desPagination,
                    pageSize,
                    pageNumber: 1
                  })
                }
                className={styles["inner-table"]}
                emptyText="No existing des keys yet"
              />
            )
          },
          {
            tabTitle: "RSA Keys",
            tabKey: "rsa",
            tabContent: (
              <Table
                columns={columns}
                dataSource={rsakeys}
                pagination={rsaPagination}
                loading={loading}
                onPageChange={pageNumber =>
                  setRsaPagination({ ...rsaPagination, pageNumber })
                }
                onPageSizeChange={pageSize =>
                  setRsaPagination({
                    ...rsaPagination,
                    pageSize,
                    pageNumber: 1
                  })
                }
                className={styles["inner-table"]}
                emptyText="No existing rsa keys yet"
              />
            )
          }
        ]}
      />
      <AddEditSymmetricKeyInfo
        visible={showAddEditModal}
        cancel={dismissAddEditModal}
        keyOnEdit={keyOnEdit}
        onComplete={() => fetchIssuerKeys(activeTab)}
        parentIssuer={issuer}
      />
      <KeyImportModal
        visible={showImportModal}
        cancel={dismissImportModal}
        onComplete={fetchIssuerKeys}
        keyOnEdit={keyOnEdit}
        issuerKeys={deskeys}
        transportKeyOptions={transportKeyOptions}
      />
      <CertificateDownloadForm
        visible={showCertificateGenerationModal}
        cancel={dismissCertificateGenerationModal}
        parentIssuer={issuer}
        keyOnEdit={keyOnEdit}
        onComplete={fetchIssuerKeys}
      />
      <CertificateUploadForm
        visible={showCertificateUploadModal}
        cancel={dismissCertificateUploadModal}
        keyOnEdit={keyOnEdit}
        caKeyOptions={caKeyOptions}
      />
      <CertificateInfoViewModal
        visible={showCertificateViewModal}
        cancel={dismissCertificateViewModal}
        parentIssuer={issuer}
        keyOnEdit={keyOnEdit}
        caKeyOptions={caKeyOptions}
      />
      <KeyValueModal
        keyOnEdit={keyOnView}
        parentIssuer={issuer}
        cancel={() => setKeyOnView(null)}
        visible={Boolean(keyOnView)}
      />
    </div>
  );
};

interface AddEditSymmetricKeyProps {
  visible: boolean;
  cancel: () => void;
  onComplete: () => void;
  keyOnEdit: AppKey | null;
  parentIssuer: Issuer | null;
}
const blankKey: AppKeyCreate = {
  keyLabel: "",
  keyValue: null,
  keyVersion: "",
  keyIndex: "",
  keyLength: 0,
  expiryDate: "",
  envFlag: "TEST",
  desKeyInfoId: 0
};

const AddEditSymmetricKeyInfo: FunctionComponent<AddEditSymmetricKeyProps> = ({
  visible,
  cancel,
  onComplete,
  keyOnEdit,
  parentIssuer
}) => {
  const [formData, setFormData] = useState<AppKeyCreate>({ ...blankKey });
  const [loading, setLoading] = useState(false);
  const [keyInfoRecords, setKeyInfoRecords] = useState<KeyCategory[]>([]);
  const [selectedKeyInfo, setSelectedKeyInfo] = useState<null | KeyCategory>(
    null
  );

  const { notify } = useContext(LayoutContext);

  const keyInfoOptions = useMemo(() => {
    return keyInfoRecords.map(record => ({
      value: record.id,
      label: record.keyCategory
    }));
  }, [keyInfoRecords]);

  const fetchKeyInfoRecords = async () => {
    const { error, message, data } = await getKeyCategories({
      pageNumber: 1,
      pageSize: 20000
    });
    if (error || !data) {
      notify("error", `Unable to fetch key information records: ${message}`);
      return;
    }

    setKeyInfoRecords([
      {
        createdOn: null,
        description: "",
        keyCategory: "RSA",
        keyType: "",
        id: -1,
        keyTypeCode: "",
        lastUpdatedOn: null
      },
      ...data.categories
    ]);
  };

  useEffect(() => {
    fetchKeyInfoRecords();
  }, []);

  const handleChange = (key: keyof AppKeyCreate, value: unknown) => {
    if (key === "desKeyInfoId") {
      setSelectedKeyInfo(
        keyInfoRecords.find(record => record.id === Number(value)) || null
      );
    }
    setFormData({ ...formData, [key]: value });
  };

  useEffect(() => {
    handleChange(
      "keyLabel",
      `${selectedKeyInfo?.keyCategory}.${formData.envFlag}.${formData.keyVersion}`
    );
  }, [formData.desKeyInfoId, formData.envFlag, formData.keyVersion]);

  useEffect(() => {
    setFormData({ ...blankKey, ...(keyOnEdit || {}) });
    setSelectedKeyInfo(
      keyOnEdit
        ? keyInfoRecords.find(record => record.id === keyOnEdit.desKeyInfoId) ||
            null
        : null
    );
  }, [visible, keyOnEdit, keyInfoRecords]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const requiredFields: (keyof AppKeyCreate)[] = [
      "desKeyInfoId",
      "keyVersion",
      "keyIndex",
      "keyLength",
      "expiryDate",
      "envFlag"
    ];
    const requiredFieldsRSA = [
      ...requiredFields,
      "keyExponentValue",
      "issuerBin"
    ].filter(field => field !== "keyType") as (keyof AppKeyCreate)[];
    const errorField = (
      selectedKeyInfo?.keyCategory === "RSA"
        ? requiredFieldsRSA
        : requiredFields
    )
      .find(field => !formData[field])
      ?.replace(/^./, char => char.toUpperCase())
      .replace(/[A-Z]/g, char => ` ${char}`)
      .trim();
    if (errorField) {
      notify("error", `${errorField} is required`);
      return;
    }

    if (
      formData.expiryDate.length !== 4 ||
      !Number(formData.expiryDate.slice(0, 2)) ||
      Number(formData.expiryDate.slice(0, 2)) > 12
    ) {
      notify("error", "Expiry date must be in the format mmYY");
      return;
    }

    setLoading(true);
    const { error, message } = await (keyOnEdit
      ? updateIssuerKey(
          { ...(keyOnEdit || {}), ...formData },
          parentIssuer?.issuerCode || "",
          selectedKeyInfo as KeyCategory
        )
      : createIssuerKey(
          { ...(keyOnEdit || {}), ...formData },
          parentIssuer?.issuerCode || "",
          selectedKeyInfo as KeyCategory
        ));
    setLoading(false);

    if (error) {
      notify(
        "error",
        `Unable to ${keyOnEdit ? "update" : "create"} issuer key: ${message}`
      );
      return;
    }
    onComplete();
    cancel();
  };

  return (
    <Modal visible={visible} cancel={cancel} size="small" ignoreBackdropClicks>
      <form onSubmit={onSubmit}>
        <div className="input-group">
          <span className="question">Key Information</span>
          <Select
            value={formData.desKeyInfoId || ""}
            placeholder="Select key information"
            onSelect={value => handleChange("desKeyInfoId", value)}
            options={keyInfoOptions}
            responsive
          />
        </div>

        <div className="input-group">
          <span className="question">Key Version</span>
          <Input
            value={formData.keyVersion}
            placeholder="Enter key version"
            onChange={value => handleChange("keyVersion", value)}
            inputValidation={{
              required: true,
              transform: val => val.replace(/\D/g, "").slice(0, 3)
            }}
          />
        </div>

        <div className="input-group">
          <span className="question">Key Index</span>
          <Input
            value={formData.keyIndex}
            placeholder="Enter key index"
            onChange={value => handleChange("keyIndex", value)}
            inputValidation={{
              required: true,
              transform: val => val.replace(/\D/g, "").slice(0, 4)
            }}
          />
        </div>

        <div className="input-group">
          <span className="question">
            Key {selectedKeyInfo?.keyCategory === "RSA" ? "Modulus" : ""} Length
          </span>
          {selectedKeyInfo?.keyCategory !== "RSA" && (
            <Select
              value={String(formData.keyLength || "")}
              placeholder="Select key length"
              onSelect={value => handleChange("keyLength", value)}
              options={getOptionsFromArray(["16", "32", "48"])}
              responsive
            />
          )}
          {selectedKeyInfo?.keyCategory === "RSA" && (
            <Input
              value={formData.keyLength || ""}
              placeholder="Enter key modulus length"
              onChange={value => handleChange("keyLength", value)}
              inputValidation={{
                required: true,
                transform: val => val.replace(/\D/g, "").slice(0, 4)
              }}
            />
          )}
        </div>

        {selectedKeyInfo?.keyCategory === "RSA" && (
          <div className="input-group">
            <span className="question">Key Exponent Value</span>
            <Select
              value={formData.keyExponentValue || ""}
              placeholder="Enter key exponent value"
              onSelect={value => handleChange("keyExponentValue", value)}
              options={getOptionsFromArray(["3", "65537"])}
              responsive
            />
          </div>
        )}

        {selectedKeyInfo?.keyCategory === "RSA" && (
          <div className="input-group">
            <span className="question">Issuer Bin</span>
            <Input
              value={formData.issuerBin || ""}
              placeholder="Enter issuer bin"
              onChange={value => handleChange("issuerBin", value)}
              inputValidation={{
                required: true,
                transform: val => val.replace(/\D/g, "").slice(0, 148)
              }}
            />
          </div>
        )}

        {selectedKeyInfo?.keyCategory === "RSA" && (
          <div className="input-group">
            <span className="question">Key Description</span>
            <Input
              value={formData.keyDescription || ""}
              placeholder="Enter key description"
              onChange={value => handleChange("keyDescription", value)}
            />
          </div>
        )}

        <div className="input-group">
          <span className="question">Expiration date (mmYY)</span>
          <Input
            value={formData.expiryDate}
            placeholder="Enter expiry date"
            onChange={value => handleChange("expiryDate", value)}
            inputValidation={{
              required: true,
              transform: val => val.replace(/\D/g, "").slice(0, 148)
            }}
            endSymbol={
              formData.expiryDate.length === 4 && (
                <small>
                  <em className="light thin smaller margin-right">
                    {
                      allMonths[
                        (Number(formData.expiryDate.slice(0, 2)) || 1000) - 1
                      ]
                    }{" "}
                    20
                    {formData.expiryDate.slice(2)}
                  </em>
                </small>
              )
            }
          />
        </div>

        <div className="input-group">
          <span className="question">Environment Flag</span>
          <Select
            value={formData.envFlag}
            placeholder="Select environment flag"
            onSelect={value => handleChange("envFlag", value)}
            options={getOptionsFromArray(["TEST", "LIVE"])}
            dropdownOnTop
            responsive
          />
        </div>

        <div className="margin-top flex end spaced large">
          <Button type="accent" onClick={cancel} size="small">
            Cancel
          </Button>
          <Button loading={loading} buttonType="submit" size="small">
            {keyOnEdit ? "Update" : "Create"} Key Information
          </Button>
        </div>
      </form>
    </Modal>
  );
};

interface KeyImportModalProps {
  visible: boolean;
  cancel: () => void;
  onComplete: () => void;
  keyOnEdit: AppKey | null;
  issuerKeys: AppKey[];
  transportKeyOptions: { label: string; value: string }[];
}

const blankKeyImportData: AppKeyImport = {
  keyLabel: "",
  keyValue: "",
  kcv: "",
  encryptionMode: "ECB",
  parentLabel: "",
  keyScheme: ""
};

const KeyImportModal: FunctionComponent<KeyImportModalProps> = ({
  visible,
  cancel,
  onComplete,
  keyOnEdit,
  issuerKeys,
  transportKeyOptions
}) => {
  const [formData, setFormData] = useState({ ...blankKeyImportData });
  const [loading, setLoading] = useState(false);
  const { notify, confirm } = useContext(LayoutContext);

  const kcvRef = useRef<HTMLInputElement>(null);
  const keyValueRef = useRef<HTMLInputElement>(null);

  const handleChange = (key: keyof AppKeyImport, value: unknown) => {
    setFormData({ ...formData, [key]: value });
  };

  useEffect(() => {
    console.log("Issuer keys:", issuerKeys);
  }, [issuerKeys]);

  useEffect(() => {
    console.log("Transport Key Options:", transportKeyOptions);
  }, [transportKeyOptions]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!isKeyZMK(keyOnEdit) && !formData.parentLabel) {
      notify("error", "Transport key is required");
      return;
    }

    if (isKeyZMK(keyOnEdit) && formData.keyValue !== formData.keyValueConfirm) {
      notify(
        "error",
        `The entries for the fields: "Value" and "Confirm Value" do not match`
      );
      return;
    }

    const formattedKCV = formData.kcv.replace(/\s/g, "");
    const formattedKeyValue = formData.keyValue.replace(/\s/g, "");

    if (formattedKCV.length !== 6) {
      notify(
        "error",
        `Key Checksum Value must be 6 characters. It currently has ${formattedKCV.length} characters`
      );
      return;
    }

    if (!isKeyZMK(keyOnEdit)) {
      const validPrefixes = ["U", "X", "T", "Y", "Z"];
      if (!validPrefixes.includes(formattedKeyValue.charAt(0))) {
        notify(
          "error",
          `Key value must start with one of the following prefixes: ${validPrefixes.join(
            ", "
          )}`
        );
        return;
      }

      if (![17, 33, 49].includes(formattedKeyValue.length)) {
        notify(
          "error",
          `Key value must be 17, 33, or 49 characters. It currently has ${formattedKeyValue.length} characters`
        );
        return;
      }
    } else {
      // Validation for ZMK keys only
      if (![16, 32, 42].includes(formattedKeyValue.length)) {
        notify(
          "error",
          `ZMK key value must be 16, 32, or 42 characters. It currently has ${formattedKeyValue.length} characters`
        );
        return;
      }
    }

    const errorCharKCV = formattedKCV.match(/[^0-9A-F]/)?.[0] || "";
    const errorCharKeyValue =
      formattedKeyValue.slice(1).match(/[^0-9A-F]/)?.[0] || "";

    if (errorCharKCV || errorCharKeyValue) {
      const index =
        (errorCharKCV ? formattedKCV : formattedKeyValue.slice(1)).indexOf(
          errorCharKCV || errorCharKeyValue
        ) + 1;
      const rawIndex = (
        errorCharKCV ? formData.kcv : formData.keyValue
      ).indexOf(errorCharKCV || errorCharKeyValue);
      notify(
        "error",
        `${
          errorCharKCV ? "Key Checksum Value" : "Key Value"
        } must only have hex characters. "${
          errorCharKCV || errorCharKeyValue
        }" was found as the ${getCardinalStr(index + 1)} character`,
        6000
      );
      const input = (errorCharKCV ? kcvRef : keyValueRef).current;
      setTimeout(() => {
        input?.focus();
        input?.setSelectionRange(rawIndex, rawIndex + 1);
      }, 500);
      return;
    }

    setLoading(true);

    const { error, message } = await importIssuerKeyValue(
      formData,
      keyOnEdit as AppKey
    );

    setLoading(false);

    if (error) {
      notify("error", `Unable to import key value: ${message}`);
      return;
    }

    cancel();
    onComplete();
  };

  const confirmBeforeSubmit = (e: FormEvent) =>
    confirm({
      title: `Do you want to overwrite existing value for the key: ${keyOnEdit?.keyLabel}`,
      body: "This action is irreversible",
      onOk: async () => onSubmit(e)
    });

  useEffect(() => {
    setFormData({ ...blankKeyImportData });
  }, [visible]);

  return (
    <Modal visible={visible} cancel={cancel} size="small" ignoreBackdropClicks>
      <form onSubmit={keyOnEdit?.keyValue ? confirmBeforeSubmit : onSubmit}>
        {!isKeyZMK(keyOnEdit) && (
          <div className="input-group">
            <span className="question">Transport Key</span>
            <Select
              value={formData.parentLabel || ""}
              placeholder="Transport key"
              onSelect={value => handleChange("parentLabel", value)}
              options={transportKeyOptions}
              responsive
            />
          </div>
        )}

        <div className="input-group">
          <span className="question">Key Checksum Value</span>
          <Input
            value={formData.kcv || ""}
            placeholder="Enter key checksum value"
            onChange={value => handleChange("kcv", value)}
            refValue={kcvRef}
            inputValidation={{
              required: true,
              transform: value =>
                value
                  .toUpperCase()
                  .replace(/[A-Z0-9]{4}(?=[^\s])/g, match => `${match.trim()} `)
                  .trim()
            }}
            required
          />
        </div>

        <div className="input-group">
          <span className="question">
            {isKeyZMK(keyOnEdit) ? "Key Value" : "Encrypted Key Data"}
          </span>
          <Input
            value={formData.keyValue || ""}
            placeholder="Enter key data"
            onChange={value => handleChange("keyValue", value)}
            refValue={keyValueRef}
            inputValidation={{
              required: true,
              transform: value =>
                value
                  .toUpperCase()
                  .replace(/[A-Z0-9]{4}(?=[^\s])/g, match => `${match.trim()} `)
                  .trim()
            }}
          />
        </div>

        {isKeyZMK(keyOnEdit) && (
          <div className="input-group">
            <span className="question">Confirm Key Value</span>
            <Input
              value={formData.keyValueConfirm || ""}
              placeholder="Confirm key data"
              onChange={value => handleChange("keyValueConfirm", value)}
              inputValidation={{
                required: true,
                transform: value =>
                  value
                    .toUpperCase()
                    .replace(
                      /[A-Z0-9]{4}(?=[^\s])/g,
                      match => `${match.trim()} `
                    )
                    .trim()
              }}
            />
          </div>
        )}

        {!isKeyZMK(keyOnEdit) && (
          <div className="input-group">
            <span className="question">Encryption Mode</span>
            <Input
              value={formData.encryptionMode || ""}
              placeholder="Encryption mode [AUTO]"
              disabled
              onChange={value => handleChange("encryptionMode", value)}
            />
          </div>
        )}

        {!isKeyZMK(keyOnEdit) && (
          <div className="input-group">
            <span className="question">Key Scheme</span>
            <Select
              value={formData.keyScheme || ""}
              placeholder="Select key scheme"
              onSelect={value => handleChange("keyScheme", value)}
              options={getOptionsFromArray(["U", "X", "T", "Y", "Z"])}
              dropdownOnTop
              responsive
            />
          </div>
        )}

        <div className="vertical-margin flex end spaced">
          <Button type="accent" onClick={cancel} size="small">
            Cancel
          </Button>
          <Button loading={loading} buttonType="submit" size="small">
            Import Key Value
          </Button>
        </div>
      </form>
    </Modal>
  );
};

interface CertificatDownloadFormProps {
  visible: boolean;
  cancel: () => void;
  keyOnEdit: AppKey | null;
  parentIssuer: Issuer | null;
  onComplete: () => void;
}

const schemeOptions: Option[] = allSchemes.map(scheme => ({
  label: scheme
    .toLowerCase()
    .replace(/^./, char => char.toUpperCase())
    .replace(/_/, " "),
  value: scheme
}));

const CertificateDownloadForm: FunctionComponent<
  CertificatDownloadFormProps
> = ({ visible, cancel, keyOnEdit, parentIssuer, onComplete }) => {
  const [schemeId, setSchemeId] = useState<Scheme | null>(null);
  const [certificateSerialNumber, setCertificateSerialNumber] = useState("");
  const [issuerPublicKeyIndex, setIssuerPublicKeyIndex] = useState("");
  const [serviceIdentifier, setServiceIdentifier] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [loading, setLoading] = useState(false);

  const { notify } = useContext(LayoutContext);

  useEffect(() => {
    setSchemeId(null);
    setCertificateSerialNumber("");
    setIssuerPublicKeyIndex("");
    setServiceIdentifier("");
    setTrackingNumber("");
  }, [visible]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!schemeId) {
      notify("error", "Please select scheme");
      return;
    }
    setLoading(true);
    const request = keyOnEdit?.hasKey
      ? downloadKeyRequest({ key: keyOnEdit as AppKey, scheme: schemeId })
      : generateRSAValue(
          keyOnEdit as AppKey,
          parentIssuer?.issuerCode as string,
          {
            schemeId,
            modeFlag: "2",
            certificateSerialNumber,
            issuerPublicKeyIndex,
            serviceIdentifier,
            trackingNumber
          }
        );
    const { error, message, data } = await request;
    setLoading(false);
    if (error) {
      notify(
        "error",
        `Unable to ${
          keyOnEdit?.hasKey
            ? "download issuer certificate"
            : "generate RSA value"
        }: ${message}`
      );
      return;
    }
    if (keyOnEdit?.hasKey) {
      const link = document.createElement("a");
      link.setAttribute(
        "href",
        URL.createObjectURL(data.file)
        // `data:application/octet-stream;charset=utf-8,${JSON.stringify(data)}`
      );
      link.setAttribute(
        "download",
        data.fileName || `${keyOnEdit.keyLabel} request`
      );
      link.click();
    } else {
      onComplete();
      notify("success", "Successfully generated RSA value");
    }
    cancel();
  };

  return (
    <Modal visible={visible} cancel={cancel} size="small" ignoreBackdropClicks>
      <form onSubmit={handleSubmit}>
        <div className="input-group">
          <span className="question">Scheme</span>
          <Select
            value={schemeId || ""}
            options={schemeOptions}
            placeholder="Select Scheme"
            onSelect={value => setSchemeId(value as Scheme)}
            responsive
          />
        </div>

        {!keyOnEdit?.hasKey &&
          (schemeId === "JCB" || schemeId === "MASTERCARD") && (
            <div className="input-group">
              <span className="question">Certificate Serial Number</span>
              <Input
                value={certificateSerialNumber}
                placeholder="Enter certificate serial number"
                onChange={setCertificateSerialNumber}
                inputValidation={{
                  required: true,
                  transform: val =>
                    val
                      .toUpperCase()
                      .replace(/[^A-F0-9]/g, "")
                      .slice(0, 6)
                }}
              />
            </div>
          )}

        {!keyOnEdit?.hasKey &&
          (schemeId === "VISA" ||
            schemeId === "AMEX" ||
            schemeId === "UNION_PAY") && (
            <div className="input-group">
              <span className="question">Service Identifier</span>
              <Input
                value={serviceIdentifier}
                placeholder="Enter service identifier"
                onChange={setServiceIdentifier}
                inputValidation={{
                  required: true,
                  transform: val =>
                    val
                      .toUpperCase()
                      .replace(/[^A-F0-9]/g, "")
                      .slice(0, 8)
                }}
              />
            </div>
          )}

        {!keyOnEdit?.hasKey &&
          (schemeId === "VISA" ||
            schemeId === "AMEX" ||
            schemeId === "UNION_PAY") && (
            <div className="input-group">
              <span className="question">Tracking Number</span>
              <Input
                value={trackingNumber}
                placeholder="Enter tracking number"
                onChange={setTrackingNumber}
                inputValidation={{
                  required: true,
                  transform: val =>
                    val
                      .toUpperCase()
                      .replace(/[^A-F0-9]/g, "")
                      .slice(0, 6)
                }}
              />
            </div>
          )}

        {!keyOnEdit?.hasKey &&
          (schemeId === "JCB" || schemeId === "MASTERCARD") && (
            <div className="input-group">
              <span className="question">Issuer Public Key Index</span>
              <Input
                value={issuerPublicKeyIndex}
                placeholder="Enter issuer public key index"
                onChange={setIssuerPublicKeyIndex}
                inputValidation={{
                  required: true,
                  transform: val =>
                    val
                      .toUpperCase()
                      .replace(/[^A-F0-9]/g, "")
                      .slice(0, 6)
                }}
              />
            </div>
          )}

        <div className="vertical-margin flex end spaced end-align">
          <Button
            className={`margin-top ${schemeId ? "large" : "xl"}`}
            type="accent"
            onClick={cancel}
            size="small"
          >
            Cancel
          </Button>
          <Button loading={loading} buttonType="submit" size="small">
            {keyOnEdit?.hasKey ? "Generate IPKC Request" : "Generate RSA value"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

interface CertificateUploadFormProps {
  visible: boolean;
  cancel: () => void;
  keyOnEdit: AppKey | null;
  caKeyOptions: Option[];
}

const CertificateUploadForm: FunctionComponent<CertificateUploadFormProps> = ({
  visible,
  cancel,
  keyOnEdit,
  caKeyOptions
}) => {
  const [scheme, setScheme] = useState<Scheme | null>(null);
  const [caKeyId, setCaKeyId] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [certificateInfo, setCertificateInfo] = useState<CertifcateInfo | null>(
    null
  );

  const { notify, confirm } = useContext(LayoutContext);

  useEffect(() => {
    setFile(null);
    setScheme(null);
  }, [visible]);

  const fetchCertificateInfo = async () => {
    const { data } = await getCertificateInfo(keyOnEdit?.keyLabel as string);
    setCertificateInfo(data);
  };

  useEffect(() => {
    setCertificateInfo(null);
    if (visible && keyOnEdit) {
      fetchCertificateInfo();
    }
  }, [keyOnEdit, visible]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const handleCertificateUpload = async () => {
      if (!file) {
        notify("error", "Please upload signed certificate to continue");
        return;
      }
      if (!scheme) {
        notify("error", "Please select scheme");
        return;
      }
      if (!caKeyId) {
        notify("error", "Please select certificate authority key");
        return;
      }
      setLoading(true);
      const { error, message } = await uploadSignedCertificate({
        scheme,
        file,
        key: keyOnEdit as AppKey,
        caKeyId
      });
      setLoading(false);
      if (error) {
        notify("error", `Unable to add signed certificate: ${message}`);
        return;
      }
      notify("success", "Signed certificate added successfully");
      cancel();
    };
    if (certificateInfo) {
      confirm({
        title: `Are you sure you want to overwrite existing certificate data?`,
        body: `A certificate has already been uploaded to the key: ${keyOnEdit?.keyLabel}. Proceeding will overwrite the existing certificate data`,
        onOk: handleCertificateUpload
      });
    } else {
      handleCertificateUpload();
    }
  };

  return (
    <Modal visible={visible} cancel={cancel} size="small" ignoreBackdropClicks>
      <form onSubmit={handleSubmit}>
        <div className="input-group">
          <span className="question">Scheme</span>
          <Select
            value={scheme || ""}
            options={schemeOptions}
            placeholder="Select Scheme"
            onSelect={value => setScheme(value as Scheme)}
            responsive
          />
        </div>

        <div className="input-group">
          <span className="question">Certificate Authority Key</span>
          <Select
            value={caKeyId || ""}
            options={caKeyOptions}
            placeholder="Select CA Key"
            onSelect={value => setCaKeyId(value as number)}
            responsive
          />
        </div>

        <div className="input-group">
          <span className="question">Upload certificate</span>
          <FileUploader
            acceptedTypes={[]}
            titleText="Upload signed certificate"
            subtitle="Or drag a file to upload"
            file={file}
            onChange={setFile}
          />
        </div>

        <div className="vertical-margin flex end spaced">
          <Button type="accent" onClick={cancel} size="small">
            Cancel
          </Button>
          <Button loading={loading} buttonType="submit" size="small">
            Add certificate to issuer
          </Button>
        </div>
      </form>
    </Modal>
  );
};

interface CertificateInfoViewModalProps {
  visible: boolean;
  cancel: () => void;
  keyOnEdit: AppKey | null;
  parentIssuer: Issuer | null;
  caKeyOptions: Option[];
}

const CertificateInfoViewModal: FunctionComponent<
  CertificateInfoViewModalProps
> = ({ visible, caKeyOptions, cancel, keyOnEdit, parentIssuer }) => {
  const [loading, setLoading] = useState(false);
  const [certificateInfo, setCertificateInfo] = useState<CertifcateInfo | null>(
    null
  );

  const { notify } = useContext(LayoutContext);

  const fetchCertificateInfo = async () => {
    setLoading(true);
    const { data, error } = await getCertificateInfo(
      keyOnEdit?.keyLabel as string
    );
    setLoading(false);
    if (error) {
      notify("info", `No certificates yet to be uploaded for this key`, 6000);
      return;
    }
    setCertificateInfo(data);
  };

  useEffect(() => {
    setCertificateInfo(null);
    if (visible && keyOnEdit) {
      fetchCertificateInfo();
    }
  }, [keyOnEdit, visible]);

  const caKeyLabel = useMemo(() => {
    return certificateInfo
      ? caKeyOptions.find(
          option => option.value === certificateInfo.caPublicKeyId
        )?.label || ""
      : "";
  }, [caKeyOptions, certificateInfo]);

  return (
    <Modal visible={visible} cancel={cancel}>
      {loading && (
        <div className="flex column spaced center-align">
          <img
            className="generic-icon xl"
            alt="loading"
            src={resources.icons.LOADING_DOTS}
          />
          <em>Fetching certificate data. . .</em>
        </div>
      )}
      {!certificateInfo && !loading && (
        <div className="vertical-margin flex center">
          <em>
            No certificates yet to be uploaded for key: {keyOnEdit?.keyLabel}
          </em>
        </div>
      )}
      {certificateInfo && !loading && (
        <div>
          <div className={styles.detail}>
            <span className={styles.key}>Issuer: </span>
            <span className={styles.value}>
              {parentIssuer?.name} - {parentIssuer?.issuerCode}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>RSA Key: </span>
            <span className={styles.value}>{keyOnEdit?.keyLabel}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>CA Public Key Label: </span>
            <span className={styles.value}>{caKeyLabel}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Created On: </span>
            <span className={styles.value}>
              {certificateInfo?.createdOn.format("D MMM YYYY")}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Last Updated On: </span>
            <span className={styles.value}>
              {certificateInfo?.lastUpdatedOn.format("D MMM YYYY")}
            </span>
          </div>
        </div>
      )}
    </Modal>
  );
};

interface KeyValueModalProps {
  visible: boolean;
  cancel: () => void;
  keyOnEdit: AppKey | null;
  parentIssuer: Issuer | null;
}

const KeyValueModal: FunctionComponent<KeyValueModalProps> = ({
  visible,
  cancel,
  keyOnEdit,
  parentIssuer
}) => {
  return (
    <Modal visible={visible} cancel={cancel} size="small">
      <div>
        <div className={styles.detail}>
          <span className={styles.key}>Issuer: </span>
          <span className={styles.value}>
            {parentIssuer?.name} - {parentIssuer?.issuerCode}
          </span>
        </div>
        <div className={styles.detail}>
          <span className={styles.key}>Key Label: </span>
          <span className={styles.value}>{keyOnEdit?.keyLabel}</span>
        </div>
        {keyOnEdit?.kcv && (
          <div className={styles.detail}>
            <span className={styles.key}>KCV: </span>
            <span className={styles.value}>{keyOnEdit?.kcv}</span>
          </div>
        )}
        {keyOnEdit?.lastUpdatedOn && (
          <div className={styles.detail}>
            <span className={styles.key}>Last Updated: </span>
            <span className={styles.value}>{keyOnEdit?.lastUpdatedOn}</span>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default FintechManagement;
